import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Property } from '../../types';
import { adminHeaders } from '../../lib/adminAuth';
import './PropertiesPage.css';

import { tx } from '../../lib/adminI18n';
function PropertiesPage() {
  const { t } = useTranslation();
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingProperty, setEditingProperty] = useState<Property | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  // 重要ポイントの入力中テキスト（改行・空行・IME入力を保持するため生の文字列で持つ）
  const [highlightsText, setHighlightsText] = useState('');

  // DBのJSON文字列 → 1行1項目のテキスト
  const highlightsToText = (raw: string | null | undefined): string => {
    if (!raw) return '';
    try {
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr.join('\n') : '';
    } catch {
      return '';
    }
  };

  // 1行1項目のテキスト → DBのJSON文字列（保存時のみ整形）
  const textToHighlights = (text: string): string | null => {
    const arr = text.split('\n').map((x) => x.trim()).filter((x) => x.length > 0);
    return arr.length > 0 ? JSON.stringify(arr) : null;
  };

  // エクスポートURLを組み立てる（secret未設定ならnull）
  const buildExportUrl = (p: Property): string | null => {
    if (!p.ical_export_secret) return null;
    return `${window.location.origin}/ical/${p.id}/${p.ical_export_secret}.ics`;
  };

  const copyExportUrl = async (p: Property) => {
    const url = buildExportUrl(p);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(p.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (e) {
      console.error('copy failed', e);
    }
  };

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await fetch('/api/admin/properties', {
        headers: adminHeaders(),
      });
      if (!response.ok) {
        throw new Error('Failed to fetch properties');
      }
      const data = await response.json();
      setProperties(data);
    } catch (err) {
      console.error('Properties error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (property: Property) => {
    setEditingProperty(property);
    setHighlightsText(highlightsToText(property.highlights));
    setIsAdding(false);
  };

  const handleAdd = () => {
    setEditingProperty({
      id: '',
      name: '',
      max_guests: 4,
      base_guests: 2,
      extra_guest_fee: 1000,
      cleaning_fee: 3000,
      min_nights: 1,
      checkin_time: '15:00',
      checkout_time: '10:00',
      is_active: 1,
      ical_import_url: null,
      ical_import_url_booking: null,
      ical_export_secret: null,
      smoking_allowed: 0,
      party_allowed: 0,
      free_cancel_until_days: 30,
      highlights: null,
      detail_url: null,
      booking_prefix: null,
      abbreviation: null,
    });
    setHighlightsText('');
    setIsAdding(true);
  };

  const handleSave = async () => {
    if (!editingProperty) return;
    
    try {
      const url = isAdding 
        ? '/api/admin/properties'
        : `/api/admin/properties/${editingProperty.id}`;
      
      const method = isAdding ? 'POST' : 'PUT';
      
      const response = await fetch(url, {
        method,
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          ...editingProperty,
          highlights: textToHighlights(highlightsText),
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to save property');
      }
      
      await fetchProperties();
      setEditingProperty(null);
      setIsAdding(false);
    } catch (err) {
      console.error('Save error:', err);
    }
  };

  return (
    <div className="properties-page">
      <div className="page-header">
        <h1>{t('admin.properties')}</h1>
        <button className="btn-primary" onClick={handleAdd}>
          {t('admin.addProperty')}
        </button>
      </div>

      {loading ? (
        <div className="loading">{t('common.loading')}</div>
      ) : (
        <div className="properties-grid">
          {properties.map((property) => (
            <div key={property.id} className="property-card">
              <div className="property-header">
                <h3>{property.name}</h3>
                <span className={`status ${property.is_active ? 'active' : 'inactive'}`}>
                  {property.is_active ? tx('prop.0') : tx('prop.1')}
                </span>
              </div>
              
              <div className="property-details">
                <div className="detail-row">
                  <span>{tx('prop.2')}</span>
                  <span>{property.max_guests}{tx('prop.3')}</span>
                </div>
                <div className="detail-row">
                  <span>{tx('prop.4')}</span>
                  <span>{property.base_guests}{tx('prop.3')}</span>
                </div>
                <div className="detail-row">
                  <span>{tx('prop.5')}</span>
                  <span>¥{property.extra_guest_fee.toLocaleString()}/名</span>
                </div>
                <div className="detail-row">
                  <span>{tx('prop.6')}</span>
                  <span>¥{property.cleaning_fee.toLocaleString()}</span>
                </div>
                <div className="detail-row">
                  <span>{tx('prop.7')}</span>
                  <span>{property.min_nights ?? 1}{tx('prop.8')}</span>
                </div>
                <div className="detail-row">
                  <span>{tx('prop.9')}</span>
                  <span>{property.checkin_time}</span>
                </div>
                <div className="detail-row">
                  <span>{tx('prop.10')}</span>
                  <span>{property.checkout_time}</span>
                </div>
                <div className="detail-row">
                  <span>{tx('prop.11')}</span>
                  <span>{property.smoking_allowed ? tx('prop.12') : tx('prop.13')}</span>
                </div>
                <div className="detail-row">
                  <span>{tx('prop.14')}</span>
                  <span>{property.party_allowed ? tx('prop.15') : tx('prop.16')}</span>
                </div>
                <div className="detail-row">
                  <span>{tx('prop.17')}</span>
                  <span>{tx('prop.18')}{property.free_cancel_until_days ?? 30}{tx('prop.19')}</span>
                </div>
              </div>
              
              <div className="ical-export">
                <div className="ical-export-label">{tx('prop.20')}</div>
                {buildExportUrl(property) ? (
                  <div className="ical-export-row">
                    <code className="ical-export-url">{buildExportUrl(property)}</code>
                    <button
                      className="btn-copy"
                      onClick={() => copyExportUrl(property)}
                      title={tx('prop.21')}
                    >
                      {copiedId === property.id ? tx('prop.22') : tx('prop.23')}
                    </button>
                  </div>
                ) : (
                  <div className="ical-export-empty">
                    {tx('prop.24')}
                  </div>
                )}
              </div>

              <button 
                className="btn-edit"
                onClick={() => handleEdit(property)}
              >
                {t('admin.editProperty')}
              </button>
            </div>
          ))}
        </div>
      )}

      {editingProperty && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{isAdding ? t('admin.addProperty') : t('admin.editProperty')}</h2>
            
            <div className="form-group">
              <label>{tx('prop.25')}</label>
              <input
                type="text"
                value={editingProperty.name}
                onChange={(e) => setEditingProperty({
                  ...editingProperty,
                  name: e.target.value
                })}
              />
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>{tx('prop.26')}</label>
                <input
                  type="number"
                  value={editingProperty.max_guests}
                  onChange={(e) => setEditingProperty({
                    ...editingProperty,
                    max_guests: parseInt(e.target.value) || 0
                  })}
                />
              </div>
              
              <div className="form-group">
                <label>{tx('prop.27')}</label>
                <input
                  type="number"
                  value={editingProperty.base_guests}
                  onChange={(e) => setEditingProperty({
                    ...editingProperty,
                    base_guests: parseInt(e.target.value) || 0
                  })}
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>{tx('prop.28')}</label>
                <input
                  type="number"
                  value={editingProperty.extra_guest_fee}
                  onChange={(e) => setEditingProperty({
                    ...editingProperty,
                    extra_guest_fee: parseInt(e.target.value) || 0
                  })}
                />
              </div>
              
              <div className="form-group">
                <label>{tx('prop.29')}</label>
                <input
                  type="number"
                  value={editingProperty.cleaning_fee}
                  onChange={(e) => setEditingProperty({
                    ...editingProperty,
                    cleaning_fee: parseInt(e.target.value) || 0
                  })}
                />
              </div>

              <div className="form-group">
                <label>{tx('prop.30')}</label>
                <input
                  type="number"
                  min={1}
                  value={editingProperty.min_nights ?? 1}
                  onChange={(e) => setEditingProperty({
                    ...editingProperty,
                    min_nights: Math.max(1, parseInt(e.target.value) || 1)
                  })}
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>{tx('prop.18')}</label>
                <input
                  type="time"
                  value={editingProperty.checkin_time}
                  onChange={(e) => setEditingProperty({
                    ...editingProperty,
                    checkin_time: e.target.value
                  })}
                />
              </div>
              
              <div className="form-group">
                <label>{tx('prop.31')}</label>
                <input
                  type="time"
                  value={editingProperty.checkout_time}
                  onChange={(e) => setEditingProperty({
                    ...editingProperty,
                    checkout_time: e.target.value
                  })}
                />
              </div>
            </div>
            
            <div className="form-group">
              <label>{tx('prop.32')}</label>
              <input
                type="url"
                placeholder="https://www.airbnb.com/calendar/ical/XXXX.ics?s=..."
                value={editingProperty.ical_import_url || ''}
                onChange={(e) => setEditingProperty({
                  ...editingProperty,
                  ical_import_url: e.target.value
                })}
              />
              <small className="field-hint">{tx('prop.33')}</small>
            </div>

            <div className="form-group">
              <label>{tx('prop.icalBooking')}</label>
              <input
                type="url"
                placeholder="https://ical.booking.com/v1/export?t=..."
                value={editingProperty.ical_import_url_booking || ''}
                onChange={(e) => setEditingProperty({
                  ...editingProperty,
                  ical_import_url_booking: e.target.value
                })}
              />
              <small className="field-hint">{tx('prop.icalBookingHint')}</small>
            </div>

            <div className="form-group">
              <label>{tx('prop.34')}</label>
              <input
                type="text"
                placeholder={tx('prop.35')}
                value={editingProperty.ical_export_secret || ''}
                onChange={(e) => setEditingProperty({
                  ...editingProperty,
                  ical_export_secret: e.target.value
                })}
              />
              {!isAdding && editingProperty.ical_export_secret && (
                <small className="field-hint">
                  {tx('prop.36')}<br />
                  <code className="export-url">{`${window.location.origin}/ical/${editingProperty.id}/${editingProperty.ical_export_secret}.ics`}</code>
                </small>
              )}
              {!isAdding && !editingProperty.ical_export_secret && (
                <small className="field-hint">{tx('prop.37')}</small>
              )}
            </div>

            <h3 className="form-section-title">{tx('prop.38')}</h3>
            <div className="form-row">
              <div className="form-group">
                <label>{tx('prop.39')}</label>
                <select
                  value={editingProperty.smoking_allowed ?? 0}
                  onChange={(e) => setEditingProperty({
                    ...editingProperty,
                    smoking_allowed: parseInt(e.target.value) as 0 | 1,
                  })}
                >
                  <option value={0}>{tx('prop.13')}</option>
                  <option value={1}>{tx('prop.12')}</option>
                </select>
              </div>
              <div className="form-group">
                <label>{tx('prop.40')}</label>
                <select
                  value={editingProperty.party_allowed ?? 0}
                  onChange={(e) => setEditingProperty({
                    ...editingProperty,
                    party_allowed: parseInt(e.target.value) as 0 | 1,
                  })}
                >
                  <option value={0}>{tx('prop.16')}</option>
                  <option value={1}>{tx('prop.15')}</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>{tx('prop.41')}</label>
              <input
                type="number"
                min="0"
                value={editingProperty.free_cancel_until_days ?? 30}
                onChange={(e) => setEditingProperty({
                  ...editingProperty,
                  free_cancel_until_days: e.target.value === '' ? 30 : (parseInt(e.target.value) || 0),
                })}
              />
              <small className="field-hint">
                {tx('prop.42')}
                {tx('prop.43')}
              </small>
            </div>

            <h3 className="form-section-title">{tx('prop.44')}</h3>
            <div className="form-row">
              <div className="form-group">
                <label>{tx('prop.45')}</label>
                <input
                  type="number" step="0.1"
                  min="0"
                  value={editingProperty.area_sqm ?? ''}
                  onChange={(e) => setEditingProperty({
                    ...editingProperty,
                    area_sqm: e.target.value === '' ? null : (parseFloat(e.target.value) || 0)
                  })}
                />
              </div>
              <div className="form-group">
                <label>{tx('prop.46')}</label>
                <input
                  type="number"
                  min="0"
                  value={editingProperty.bedroom_count ?? ''}
                  onChange={(e) => setEditingProperty({
                    ...editingProperty,
                    bedroom_count: e.target.value === '' ? null : (parseInt(e.target.value) || 0)
                  })}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>{tx('prop.47')}</label>
                <input
                  type="number"
                  min="0"
                  value={editingProperty.living_room_count ?? ''}
                  onChange={(e) => setEditingProperty({
                    ...editingProperty,
                    living_room_count: e.target.value === '' ? null : (parseInt(e.target.value) || 0)
                  })}
                />
              </div>
              <div className="form-group">
                <label>{tx('prop.48')}</label>
                <input
                  type="number"
                  min="0"
                  value={editingProperty.floor_count ?? ''}
                  onChange={(e) => setEditingProperty({
                    ...editingProperty,
                    floor_count: e.target.value === '' ? null : (parseInt(e.target.value) || 0)
                  })}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>{tx('prop.49')}</label>
                <input
                  type="number"
                  min="0"
                  value={editingProperty.double_bed_count ?? ''}
                  onChange={(e) => setEditingProperty({
                    ...editingProperty,
                    double_bed_count: e.target.value === '' ? null : (parseInt(e.target.value) || 0)
                  })}
                />
              </div>
              <div className="form-group">
                <label>{tx('prop.50')}</label>
                <input
                  type="number"
                  min="0"
                  value={editingProperty.single_bed_count ?? ''}
                  onChange={(e) => setEditingProperty({
                    ...editingProperty,
                    single_bed_count: e.target.value === '' ? null : (parseInt(e.target.value) || 0)
                  })}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>{tx('prop.51')}</label>
                <input
                  type="number"
                  min="0"
                  value={editingProperty.futon_count ?? ''}
                  onChange={(e) => setEditingProperty({
                    ...editingProperty,
                    futon_count: e.target.value === '' ? null : (parseInt(e.target.value) || 0)
                  })}
                />
              </div>
              <div className="form-group">
                <label>{tx('prop.52')}</label>
                <input
                  type="number"
                  min="0"
                  value={editingProperty.double_sofabed_count ?? ''}
                  onChange={(e) => setEditingProperty({
                    ...editingProperty,
                    double_sofabed_count: e.target.value === '' ? null : (parseInt(e.target.value) || 0)
                  })}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>{tx('prop.53')}</label>
                <input
                  type="number"
                  min="0"
                  value={editingProperty.single_sofabed_count ?? ''}
                  onChange={(e) => setEditingProperty({
                    ...editingProperty,
                    single_sofabed_count: e.target.value === '' ? null : (parseInt(e.target.value) || 0)
                  })}
                />
              </div>
              <div className="form-group">
                <label>{tx('prop.54')}</label>
                <input
                  type="number"
                  min="0"
                  value={editingProperty.toilet_count ?? ''}
                  onChange={(e) => setEditingProperty({
                    ...editingProperty,
                    toilet_count: e.target.value === '' ? null : (parseInt(e.target.value) || 0)
                  })}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>{tx('prop.55')}</label>
                <input
                  type="number"
                  min="0"
                  value={editingProperty.bath_count ?? ''}
                  onChange={(e) => setEditingProperty({
                    ...editingProperty,
                    bath_count: e.target.value === '' ? null : (parseInt(e.target.value) || 0)
                  })}
                />
              </div>
            </div>

            <div className="form-group">
              <label>{tx('prop.56')}</label>
              <textarea
                rows={4}
                placeholder={tx('prop.57')}
                value={highlightsText}
                onChange={(e) => setHighlightsText(e.target.value)}
              />
              <small className="field-hint">{tx('prop.58')}</small>
            </div>

            <div className="form-group">
              <label>{tx('prop.59')}</label>
              <input
                type="url"
                placeholder="https://staydreamhotel.com/tokyo-large-group-stay-ja.html"
                value={editingProperty.detail_url || ''}
                onChange={(e) => setEditingProperty({
                  ...editingProperty,
                  detail_url: e.target.value
                })}
              />
              <small className="field-hint">{tx('prop.60')}</small>
            </div>

            <div className="form-group">
              <label>{tx('prop.61')}</label>
              <input
                type="text"
                value={editingProperty.booking_prefix || ''}
                onChange={(e) => setEditingProperty({
                  ...editingProperty,
                  booking_prefix: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')
                })}
                placeholder={tx('prop.62')}
                maxLength={4}
              />
              <small className="field-hint">{tx('prop.63')}</small>
            </div>

            <div className="form-group">
              <label>{tx('prop.64')}</label>
              <input
                type="text"
                value={editingProperty.abbreviation || ''}
                onChange={(e) => setEditingProperty({
                  ...editingProperty,
                  abbreviation: e.target.value
                })}
                placeholder={tx('prop.65')}
                maxLength={10}
              />
              <small className="field-hint">{tx('prop.66')}</small>
            </div>

            <div className="modal-actions">
              <button 
                className="btn-secondary"
                onClick={() => {
                  setEditingProperty(null);
                  setIsAdding(false);
                }}
              >
                {t('common.cancel')}
              </button>
              <button 
                className="btn-primary"
                onClick={handleSave}
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PropertiesPage;
